<template>
  <div class="md-layout">
    <div v-if="!isExtranet" class="md-layout-item md-size-100 md-subheading">
      <strong>Tamaño total de imágenes:</strong>
      {{ photosSize(activePhotos) | fileSize }}
      &nbsp;
      <strong>Visibles para el cliente:</strong>
      {{ photosSize(activePhotos.filter((p) => p.clientVisibility)) | fileSize }}
    </div>
    <tabs
      :cache-lifetime="0"
      :options="tabsOptions"
      :cacheLifetime="0"
      id="evidences-tabs"
      :class="hasRequiredPhotos ? '' : 'danger-tabs'"
      class="md-layout-item md-size-100"
      ref="tabs"
      @changed="tabChanged"
      v-if="fileTypes"
    >
      <slot name="tabs-start" :fileTypes="fileTypes" :concepts="concepts"></slot>
      <tab
        :name="fileType.name"
        :id="codename"
        v-for="(fileType, codename) in fileTypes"
        :key="fileType.id"
      >
        <p
          class="text-gray"
          v-if="fileType.description && fileType.description != fileType.name"
        >{{ fileType.description }}</p>
        <photo-evidences-list
          :value="
            !fileType.displayInfo.noConceptGrouping
              ? conceptPhotosByFileType[fileType.id]
              : nonConceptPhotosByFileType[fileType.id]
          "
          :dont-allow-client-visibility-change="
            nonVisibleFileTypes.includes(codename)
          "
          :loading="loading"
          :isES="isES"
          :isCP="isCP"
          :show-labels="fileType.displayInfo.showLabels"
          :use-file-icons="fileType.displayInfo.regularFile"
          :not-display-empty-content="!fileType.displayInfo.required"
          :concepts="!fileType.displayInfo.noConceptGrouping ? concepts : []"
          :no-image-actions="noImageActions"
          :isExtranet="isExtranet"
          @changeVisible="changeVisible"
          @toRejectPhotos="
            toRejectPhotos($event, fileType.displayInfo.noConceptGrouping)
          "
          @toEditItem="
            toEditItem($event, fileType.displayInfo.noConceptGrouping)
          "
          @deleteItem="
            deletePhoto($event, fileType.displayInfo.noConceptGrouping)
          "
          @hasAllConceptPhotos="fileType.hasAllConcepts = $event"
        >
          <loading-content
            v-if="fileType.displayInfo.required"
            slot="empty-state"
            :class="isExtranet ? '' : 'md-accent'"
            :empty-icon="
              !fileType.displayInfo.regularFile
                ? 'photo_library'
                : 'description'
            "
            :empty-label="`${fileType.name}: No hay ${
              !fileType.displayInfo.regularFile ? 'fotos' : 'archivos'
            }`"
            :empty-description="emptyDescriptionMessage({ fileType, codename })"
          />
        </photo-evidences-list>
        <div
          class="md-layout md-alignment-space-around"
          v-if="
            codename === 'panoramic' &&
            showPanoramicReject &&
            !readOnly &&
            hasPanoramicPhotos
          "
        >
          <md-checkbox
            class="md-warning md-layout-item"
            :value="!doRandomInspection"
            @change="askToDoRanomInspection"
          >Marcar siniestro para inspección.</md-checkbox>
          <md-button
            class="md-danger md-round md-sm md-layout-item md-simple"
            @click="rejectPanoramicPhotos"
          >
            <md-icon>close</md-icon>Rechazar fotos panoramicas
          </md-button>
        </div>
        <template v-if="!readOnly && showUploader({ codename })">
          <photo-concept-uploader
            :ref="`${codename}-photoUploader`"
            class="md-layout-item"
            conceptLabel="Concepto"
            :loading-content-info="{
              emptyLabel: ``,
              emptyDescription: `Haga click o arrastre ${
                fileType.displayInfo.regularFile ? 'archivos' : 'imagénes'
              } de su dispositivo.`,
              emptyIcon: fileType.displayInfo.regularFile
                ? 'description'
                : 'add_a_photo',
              loadingLabel: `Subiendo ${fileType.name}`,
            }"
            :concepts="
              !fileType.displayInfo.noConceptGrouping
                ? concepts
                : SINGLE_CONCEPT
            "
            @sendedPhotos="
              uploadEvidences($event, {
                fileTypeId: fileType.id,
                codename,
                isRegularFile: fileType.displayInfo.regularFile,
              })
            "
            @updatedSelectedConcept="newPhotoConceptId = $event"
            v-if="!isES || hasSecurityPhotos"
            :regular-file="fileType.displayInfo.regularFile"
            :loading="uploading"
          >
            <template v-slot:concept-name="{ concept }">
              {{ concept.name !== SINGLE_CONCEPT[0].name ? concept.id : "" }}
              {{ concept.name }}
            </template>
          </photo-concept-uploader>

          <loading-content
            class="md-accent"
            error
            error-icon="add_a_photo"
            :error-description="
              isExtranet
                ? ''
                : `No se pueden subir evidencias. Faltan foto de seguridad del día (${missingSecurityTypes.join(
                    ', '
                  )}).`
            "
            v-else
          />
        </template>
      </tab>
    </tabs>

    <!-- :TODO: add reject types -->
    <DialogRejectFiles
      class="dialog-reject"
      :dialog="dialog1"
      @rejectFiles="rejectPhotos"
      @close="dialog1 = false"
      only-free-text
    />

    <input ref="imgFile" type="file" style="display: none" @change="onChangeImage" />
  </div>
</template>

<script>
import PhotoEvidencesList from "./PhotoEvidencesList";
import { PhotoConceptUploader, DialogRejectFiles } from "@/components";

import catalogService from "@/services/catalog";
import fileService from "@/services/Files";
import workPlanService from "@/services/workPlan";
import sinisterService from "@/services/sinister";
import purchaseService from "@/services/purchase";

import { PHOTO_TYPES } from "./WeeklyExecution/utils";
import {
  readImage,
  writeImage,
  reduceImageQuality,
  iterateDelayed,
} from "@/utils";

const filesGroupedByFiletype = (files) => {
  const obj = {};
  for (const file of files) {
    const id = file.fileTypeId;
    let arr = obj[id];

    if (!arr) {
      arr = [];
      obj[id] = arr;
    }
    arr.push(file);
  }

  return obj;
};

const REQUIRED_SECURITY_PHOTOS = ["COIA"];

const REQUIRED_PHOTOS_MESSAGE = "Fotos requeridas";
const REQUIRED_FILES_MESSAGE = "Archivos requeridos";
const MISSING_REQUIRED_PHOTOS_MESSAGE =
  "No se puede subir hasta subir evidencias requeridas";

export default {
  name: "PhotoEvidences",

  components: {
    PhotoEvidencesList,
    PhotoConceptUploader,
    DialogRejectFiles,
  },

  props: {
    affectedId: {
      type: Number,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    isES: {
      type: Boolean,
      default: false,
    },
    isCP: {
      type: Boolean,
      default: false,
    },
    showPanoramicReject: {
      type: Boolean,
      default: false,
    },
    // workPlanId: {
    //   type: [Number, Boolean],
    //   default: false
    // },
    noImageActions: {
      type: Boolean,
      default: false,
    },
    isExtranet: {
      default: false,
      type: Boolean,
    },
    checkDate: {
      type: String,
      required: false,
    },
    photoFileTypes: {
      type: Array,
      required: false,
    },
    realPlan: {
      type: Object,
      required: false,
    },
  },

  computed: {
    newPhotos() {
      const toUploadPhotos = this.toUploadPhotos || [];
      const conceptId = this.newPhotoConceptId;
      const currentFileType = this.currentFileType;

      if (!conceptId || !currentFileType) {
        return;
      }

      const fileType = this.fileTypes[currentFileType].id;

      return toUploadPhotos.map((photo) => ({
        ...photo,
        conceptId,
        fileType,
      }));
    },
    // Rejected photos never reach the expediente
    activePhotos() {
      return [...this.conceptPhotos, ...this.nonConceptPhotos].filter(
        p => !p.isRejected
      );
    },
    conceptPhotosByFileType() {
      return filesGroupedByFiletype(this.conceptPhotos);
    },
    nonConceptPhotosByFileType() {
      return filesGroupedByFiletype(
        this.nonConceptPhotos.map((f) => ({
          ...f,
          fileTypeId: f.fileTypeId,
        }))
      );
    },
    tabsOptions() {
      return {
        useUrlFragment: false,
        defaultTabHash: this.currentTab,
      };
    },
    missingSecurityTypes() {
      const currentTypes = this.securityPhotos.map(
        ({ reason: { code } }) => code
      );
      const missing = REQUIRED_SECURITY_PHOTOS.filter(
        (r) => !currentTypes.includes(r)
      );
      return missing;
    },
    hasSecurityPhotos() {
      return this.missingSecurityTypes.length === 0;
    },
    hasRequiredPhotos() {
      const { fileTypes = {}, photoTypes = [], nonConceptPhotos = [] } = this;
      let missingRequired = [];
      if (!fileTypes) {
        return false;
      }
      missingRequired = photoTypes.filter(
        ({ required, noConceptGrouping, fileCode }) =>
          required
            ? !noConceptGrouping
              ? !fileTypes[fileCode].hasAllConcepts
              : !nonConceptPhotos.some((f) => f.codename === fileCode)
            : false
      );
      return missingRequired.length === 0;
    },
    hasPanoramicPhotos() {
      // :TODO: This should be panoramic photos
      const { nonConceptPhotos } = this;
      return nonConceptPhotos.length > 0;
    },
    affected() {
      const { planExpected } = this.realPlan;
      return planExpected ? planExpected.affected : {};
    },
    workPlanId() {
      const { planExpectedId } = this.realPlan;
      return planExpectedId;
    },
  },

  methods: {
    photosSize(photos) {
      return photos.reduce((total, p) => total + (p.size || 0), 0);
    },
    fetchFileTypes() {
      const { photoTypes } = this;
      catalogService.fetch("common/filetype").then((fileTypes) => {
        const fTypes = {};
        photoTypes.map(({ fileCode, ...displayInfo }) => {
          let fileType = fileTypes.find((f) => f.codename == fileCode);

          if (!fileType) {
            fileType = {
              id: fileCode, // used for keying
              codename: displayInfo.missingFileLabel || fileCode,
              name: fileCode,
            };
          }

          fTypes[fileCode] = {
            ...fileType,
            file: null,
            hasAllConcepts: false,
            displayInfo,
          };
        });

        this.fileTypes = fTypes;
      });
    },
    fetchQuantification() {
      this.loading = true;
      this.error = false;
      return purchaseService
        .fetchQuantification(this.affectedId)
        .then((quantification) => {
          if (!quantification) {
            this.error = true;
            return;
          }
          this.loading = false;
          this.concepts = quantification.concepts.filter((c) => !c.notToDo);
        })
        .catch((err) => {
          this.error = true;
        });
    },
    fetchEvidencePhotos() {
      const { isExtranet } = this;
      const filesUrl = isExtranet ? "customer_conceptfiles" : "conceptfiles";
      this.loadingPhotos = true;
      return catalogService
        .fetch(`common/${filesUrl}/byAffected`, {
          id: this.affectedId,
          collection: true,
        })
        .then((photos) => {
          this.conceptPhotos = [
            ...photos.map((p) => ({
              ...p,
              label: p.addDate,
            })),
          ];
        })
        .finally(() => {
          this.loadingPhotos = false;
        });
    },
    fetchEvidenceNonConceptPhotos() {
      const { isExtranet, photoTypes } = this;
      const filesUrl = isExtranet ? "get_customer_files" : "getfile";
      this.loadingPhotos = true;
      const NON_CONCEPTS_PHOTO_TYPES = photoTypes
        .filter(({ noConceptGrouping }) => !!noConceptGrouping)
        .map(({ fileCode }) => fileCode);
      return catalogService
        .fetch(`common/file/${filesUrl}`, {
          id: this.affectedId,
          collection: true,
        })
        .then((photos) => {
          this.nonConceptPhotos = photos.filter(({ codename: type }) =>
            NON_CONCEPTS_PHOTO_TYPES.includes(type)
          );
        })
        .finally(() => {
          this.loadingPhotos = false;
        });
    },
    uploadEvidences(photos, { fileTypeId, codename, isRegularFile }) {
      const { newPhotoConceptId: conceptId } = this;
      const evidencePhotos = photos.map((photo) => ({
        ...photo,
        conceptId,
        fileType: fileTypeId,
      }));
      this.$nextTick(() =>
        this.sendPhotos(evidencePhotos, codename, isRegularFile)
      );
    },
    sendPhoto(photo) {
      return catalogService
        .create("common/conceptfiles", photo)
        .catch((err) => {
          const { requiredBTC, requiredGO } = this.affected;
          if ((requiredBTC && requiredGO) || !this.hasRequiredPhotos) {
            this.notifyError(err);
          } else {
            throw new Error("Error subiendo foto");
          }
        });
    },
    sendNonConceptPhoto({ fileType, file, ...photo }) {
      const { affectedId: affected } = this;
      const photoPayload = {
        file,
        fileType,
        affected,
      };
      return fileService.create(photoPayload).catch((err) => {
        const { requiredBTC, requiredGO } = this.affected;
        if ((requiredBTC && requiredGO) || !this.hasRequiredPhotos) {
          this.notifyError(err);
        } else {
          throw err;
        }
      });
    },
    sendPhotos(evidencePhotos, codename, isRegularFile) {
      if (!evidencePhotos) {
        return;
      }
      const [photoUploader] = this.$refs[`${codename}-photoUploader`];
      const currentFileType = this.currentFileType;
      this.uploading = true;
      // :TODO: check correct filety
      const evidenceTypeLabel = !isRegularFile ? "Evidencias" : "Evidencias";
      const {
        displayInfo: { noConceptGrouping },
      } = this.fileTypes[codename];
      const uploadPromise = !noConceptGrouping
        ? this.sendPhoto
        : this.sendNonConceptPhoto;
      return iterateDelayed(evidencePhotos, 500, uploadPromise)
        .then(() => {
          this.currentFileType = null;
          this.$emit("uploadedPhotos");
          // :TODO: Only update upload photo type
          this.fetchAllPhotos();
          this.$notify({
            message: `${evidenceTypeLabel} cargadas en sistema`,
            icon: "check",
            horizontalAlign: "center",
            verticalAlign: "bottom",
            type: "success",
          });
          photoUploader.unloadPhotos();
        })
        .catch((err) => {
          this.$notify({
            message: `ERROR cargando imagénes ${err}`,
            icon: "add_alert",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "danger",
          });
        })
        .finally(() => {
          this.currentTab = currentFileType;
          this.uploading = false;
        });
    },

    changeVisible(file) {
      this.loading = true;
      return workPlanService
        .updateCustomerVisibility([file])
        .then((files) => {
          const index = this.conceptPhotos.findIndex(
            ({ fileId }) => fileId === files[0].id
          );
          this.conceptPhotos[index].clientVisibility =
            !this.conceptPhotos[index].clientVisibility;
        })
        .catch(this.$fireErrorResponse)
        .finally(() => (this.loading = false));
    },
    toRejectPhotos(photos, nonConceptPhotos) {
      this.rejectPhotosList = [...photos];
      this.rejectingNonConceptFiles = nonConceptPhotos;
      this.dialog1 = true;
    },
    rejectPhotos(comment) {
      this.$confirmMessage(
        { title: "¿Realmente desea rechazar las fotos seleccionadas?" },
        () => {
          const rejectPromise = this.rejectingNonConceptFiles
            ? sinisterService.rejectFiles
            : workPlanService.rejectEvidencePhotos;
          return rejectPromise({
            [this.rejectingNonConceptFiles ? "files" : "concept_files"]: [
              ...this.rejectPhotosList,
            ],
            comment,
          })
            .then(() => {
              this.$emit("refresh");
              this.fetchAllPhotos();
              this.dialog1 = false;
              this.$successMessage({ text: "Fotos rechazadas" });
            })
            .catch(this.$fireErrorResponse);
        }
      );
    },
    updateEvidencePhotos(payload, noConceptPhoto) {
      return workPlanService
        .updateEvidencePhotos(payload, noConceptPhoto)
        .then(() => {
          this.$emit("refresh");
          this.fetchAllPhotos();
          this.toEditPhoto = null;
          this.$successMessage({ title: `Foto cambiada` });
        })
        .catch(this.$fireErrorResponse);
    },

    toEditItem(photo, noConceptPhoto) {
      if (noConceptPhoto) {
        this.toEditNonConceptPhoto = photo;
      } else {
        this.toEditPhoto = photo;
      }
      this.$refs.imgFile.click();
    },

    onChangeImage(e) {
      const files = [...e.target.files];
      e.target.value = "";
      const [ffile] = files;
      const ext = ffile ? ffile.name.split(".").pop() : null;
      const validExt = ["jpg", "jpeg", "png"];
      if (ffile && validExt.some((v) => v === ext)) {
        readImage(ffile).then((img) => {
          reduceImageQuality(img).then((reduced) => {
            writeImage(reduced).then((res) => {
              const { toEditPhoto, toEditNonConceptPhoto } = this;
              const formData = new FormData();
              formData.append("file", res);
              // concept photo uses the id in the body
              if (toEditPhoto) {
                formData.append("concept_id", toEditPhoto);
              }
              this.updateEvidencePhotos(formData, toEditNonConceptPhoto);
            });
          });
        });
      } else {
        this.$alertMessage({ title: "Formato de archivo incorrecto" });
      }
    },

    tabChanged({ tab }) {
      const tId = tab.id;
      this.currentTab = tId;
    },
    fetchSecurityPhotos() {
      const { affectedId: affected, checkDate } = this;
      return workPlanService
        .fetchSecurityPhotos({
          affected,
          day: checkDate,
        })
        .then((securityPhotos) => {
          this.securityPhotos = securityPhotos;
        })
        .catch(this.$fireErrorResponse);
    },
    rejectPanoramicPhotos() {
      const { workPlanId } = this;
      return workPlanService
        .rejectPanoramicPhotos({ id: workPlanId })
        .then(() => this.$successMessage({ title: "Foto rechazada" }))
        .catch(this.$fireErrorResponse);
    },
    askToDoRanomInspection() {
      this.$confirmMessage({ title: "Confirmar inspección aleatoria?" }, () => {
        const { workPlanId } = this;
        workPlanService
          .markSinisterToRevision({
            workPlanExpectId: workPlanId,
          })
          .then(() => {
            this.doRandomInspection = true;
          })
          .catch(this.$fireErrorResponse);
      });
    },
    notifyError(err, notifyOptions) {
      this.$notify({
        message: `Error subiendo foto: ${err}`,
        icon: "add_alert",
        horizontalAlign: "center",
        verticalAlign: "top",
        type: "danger",
        ...notifyOptions,
      });
    },
    fetchAllPhotos() {
      this.fetchEvidencePhotos();
      this.fetchEvidenceNonConceptPhotos();
      if (this.$can("view", "WorkPlan", "securityphotos")) {
        this.fetchSecurityPhotos();
      }
    },
    emptyDescriptionMessage({ fileType, codename }) {
      const { hasRequiredPhotos, isExtranet, readOnly } = this;
      if (codename === "panoramic" && !hasRequiredPhotos) {
        return MISSING_REQUIRED_PHOTOS_MESSAGE;
      } else if (isExtranet || readOnly) {
        return "";
      }
      return !fileType.displayInfo.regularFile
        ? REQUIRED_PHOTOS_MESSAGE
        : REQUIRED_FILES_MESSAGE;
    },
    showUploader({ codename }) {
      const { fileTypes = {}, photoTypes = [], nonConceptPhotos = [] } = this;
      let missingRequired = [];
      if (!fileTypes) {
        return false;
      }
      missingRequired = photoTypes
        .filter(({ fileCode }) => fileCode !== "panoramic")
        .filter(({ required, noConceptGrouping, fileCode }) =>
          required
            ? !noConceptGrouping
              ? !fileTypes[fileCode].hasAllConcepts
              : !nonConceptPhotos.some((f) => f.codename === fileCode)
            : false
        );
      return codename !== "panoramic" || missingRequired.length === 0;
    },
    uploadRegularFiles(e, fileTypeInfo) {
      const files = e.target.files || e.dataTransfer.files;
      if (!files.length) return; // Multiples Images selected
      const reader = new FileReader();
      reader.onload = (file) => {
        this.uploadEvidences([{ file }], fileTypeInfo);
      };
      reader.readAsDataURL(files[0]);
    },
    deletePhoto(itemId, noConceptPhoto) {
      this.$confirmMessage(
        {
          title: "¿Esta seguro de que desea borrar la evidencia seleccionada?",
        },
        () => {
          const deletePromise = !noConceptPhoto
            ? catalogService.delete("common/conceptfiles", { id: itemId })
            : fileService.delete({ id: itemId });
          return deletePromise
            .then(() => {
              this.fetchAllPhotos();
              this.$notify({
                message: `Se ha borrado la evidencia seleccionada`,
                icon: "check",
                horizontalAlign: "center",
                verticalAlign: "bottom",
                type: "success",
              });
            })
            .catch(this.$fireErrorResponse);
        }
      );
    },
  },

  watch: {
    affectedId: {
      immediate: true,
      handler(id) {
        if (!id) return;

        this.fetchQuantification();
        this.fetchAllPhotos();
      },
    },
    currentTab(value) {
      this.$refs.tabs.selectTab(`#${value}`);
      this.currentFileType = value;
    },
    hasRequiredPhotos: {
      immediate: true,
      handler(value) {
        this.$emit("hasRequiredPhotos", value);
      },
    },
  },

  mounted() {
    this.fetchFileTypes();
  },

  data() {
    const photoTypes = this.photoFileTypes ? this.photoFileTypes : PHOTO_TYPES;
    return {
      loading: false,
      photoTypes: photoTypes.filter((type) =>
        this.isExtranet
          ? !["ALMSAL", "RETSAL", "panoramic"].includes(type.fileCode)
          : !!type
      ),
      nonVisibleFileTypes: ["ALMSAL", "RETSAL"],
      uploading: false,
      newPhotoConceptId: null,
      toUploadPhotos: [],
      concepts: [],
      nonConceptPhotos: [],
      conceptPhotos: [],
      roadSinister: null,
      fileTypes: null,
      currentFileType: photoTypes[0].fileCode,
      currentTab: photoTypes[0].fileCode,
      dialog1: false,
      rejectPhotosList: [],
      rejectingNonConceptFiles: null,
      toEditPhoto: null,
      toEditNonConceptPhoto: null,
      loadingPhotos: true,
      securityPhotos: [],
      doRandomInspection: null,
      SINGLE_CONCEPT: [
        {
          id: 99999,
          name: "Evidencias",
        },
      ],
    };
  },
};
</script>

<style lang="css" scoped>
#evidences-tabs .tabs-component-panels {
  margin-top: 0em !important;
  padding-top: 0em;
}
.dialog-reject {
  z-index: 110;
}
.tabs-component {
  margin-top: 0px;
}
</style>



// WEBPACK FOOTER //
// src/pages/WorkPlan/PhotoEvidences.vue